import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useConversation } from '@elevenlabs/react';
import { BarVisualizerDemo } from '../components/BarVisualizerDemo';
import './Jobs.css';

const ADD_JOB_STEPS = [
  'Fetching job details from source',
  'Analyzing resume & generating questions',
  'Saving job to your account',
  'Job uploaded successfully! Redirecting to job details...'
];

// ElevenLabs Agent ID from environment variable
// IMPORTANT: Make sure this agent:
// 1. Exists in your ElevenLabs account (https://elevenlabs.io/app/agents)
// 2. Is set to "Public" or you have proper authentication configured
// 3. Has a valid configuration (prompt, first message, voice, etc.)
// 4. Is not archived or disabled
const AGENT_ID = process.env.REACT_APP_ELEVENLABS_AGENT_ID; // Agent configuration for interview practice

if (!AGENT_ID) {
  console.error('Missing REACT_APP_ELEVENLABS_AGENT_ID environment variable. Please check your .env file.');
}

const Jobs = () => {
  // State management for jobs and UI
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [questions, setQuestions] = useState({
    story_impact: [],
    thinking_skills: [],
    composure_pressure: []
  });
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [labActiveTab, setLabActiveTab] = useState('story');
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [agentState, setAgentState] = useState('listening');
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationStartTime, setConversationStartTime] = useState(null);
  const [analyzingAnswer, setAnalyzingAnswer] = useState(false);
  const [answerAnalysis, setAnswerAnalysis] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedQuestionForFeedback, setSelectedQuestionForFeedback] = useState(null);
  const [jobDescriptionUrl, setJobDescriptionUrl] = useState('');
  const [jobSource, setJobSource] = useState('linkedin'); // 'linkedin' | 'other'
  const [addingJob, setAddingJob] = useState(false);
  const [addJobStep, setAddJobStep] = useState(1);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pendingFeedbackParams, setPendingFeedbackParams] = useState(null);
  const [pendingPracticeParams, setPendingPracticeParams] = useState(null);
  
  // Initialize ElevenLabs conversation
  const conversation = useConversation({
    onConnect: () => {
      console.log('✅ Connected to ElevenLabs agent');
      setAgentState('listening');
    },
    onDisconnect: (event) => {
      console.log('Disconnected from agent', event);
      if (event?.reason) {
        console.log('Disconnect reason:', event.reason);
      }
      if (event?.message) {
        console.log('Disconnect message:', event.message);
      }
      setAgentState('listening');
      setCurrentConversationId(null);
    },
    onError: (error) => {
      console.error('ElevenLabs error:', error);
      try {
        const errorMessage = error?.message || error?.error?.message || String(error) || 'Connection error';
        if (errorMessage.includes('agent') || errorMessage.includes('not found')) {
          alert(`Agent Error: Please verify the agent ID "${AGENT_ID}" exists and is accessible.`);
        }
      } catch (e) {
        console.error('Error parsing error object:', e);
      }
      setAgentState('listening');
    },
    onModeChange: ({ mode }) => {
      console.log('Mode changed:', mode);
      setAgentState(mode === 'speaking' ? 'speaking' : mode === 'thinking' ? 'thinking' : 'listening');
    },
    onMessage: (message) => {
      console.log('Message received:', message);
    },
    onStatusChange: ({ status }) => {
      console.log('Status changed:', status);
      if (status === 'connecting') {
        setAgentState('connecting');
      } else if (status === 'connected') {
        setAgentState('listening');
      } else if (status === 'disconnected') {
        setAgentState('listening');
      }
    },
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  // Handle navigation query params from Home
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Open Add Job modal when coming from Home "Upload" CTA
    if (params.get('addJob') === '1') {
      setShowAddJobModal(true);
    }

    // Open Interview Lab tab on the first job when coming from Home "Answer your first questions"
    if (params.get('openLabForFirstJob') === '1' && jobs.length > 0 && !selectedJob) {
      const firstJob = jobs[0];
      setSelectedJob(firstJob);
      setActiveTab('lab');
    }
    
    // Open feedback panel for a specific answered question
    const feedbackQuestionId = params.get('feedbackQuestionId');
    const feedbackJobId = params.get('jobId');
    if (feedbackQuestionId && feedbackJobId) {
      setPendingFeedbackParams({
        questionId: feedbackQuestionId,
        jobId: feedbackJobId,
      });

      // If no job selected yet, select the matching job and go to lab tab
      if (!selectedJob && jobs.length > 0) {
        const targetJob = jobs.find((j) => j.id === feedbackJobId) || jobs[0];
        setSelectedJob(targetJob);
        setActiveTab('lab');
      }
    }

    // Open practice modal for a specific question
    const practiceQuestionId = params.get('startPractice');
    const practiceJobId = params.get('jobId');
    if (practiceQuestionId && practiceJobId) {
      setPendingPracticeParams({
        questionId: practiceQuestionId,
        jobId: practiceJobId,
      });

      // If no job selected yet, select the matching job and go to lab tab
      if (!selectedJob && jobs.length > 0) {
        const targetJob = jobs.find((j) => j.id === practiceJobId) || jobs[0];
        setSelectedJob(targetJob);
        setActiveTab('lab');
      }
    }
  }, [location.search, jobs, selectedJob]);

  // Once questions are loaded for the selected job, open feedback panel if requested
  useEffect(() => {
    if (!pendingFeedbackParams || !selectedJob) return;

    const { questionId, jobId } = pendingFeedbackParams;

    if (selectedJob.id !== jobId) return;

    // Flatten questions by category to find the matching one
    const allQuestions = [
      ...questions.story_impact,
      ...questions.thinking_skills,
      ...questions.composure_pressure,
    ];

    const targetQuestion = allQuestions.find((q) => q.id === questionId);

    if (targetQuestion) {
      // Use existing handler to open the feedback side panel
      handleViewFeedback(targetQuestion);
      setPendingFeedbackParams(null);
    }
  }, [pendingFeedbackParams, selectedJob, questions]);

  // Once questions are loaded for the selected job, open practice modal if requested
  useEffect(() => {
    if (!pendingPracticeParams || !selectedJob) return;

    const { questionId, jobId } = pendingPracticeParams;

    if (selectedJob.id !== jobId) return;

    // Flatten questions by category to find the matching one
    const allQuestions = [
      ...questions.story_impact,
      ...questions.thinking_skills,
      ...questions.composure_pressure,
    ];

    const targetQuestion = allQuestions.find((q) => q.id === questionId);

    if (targetQuestion) {
      // Use existing handler to open the practice modal
      handleStartPractice(targetQuestion);
      setPendingPracticeParams(null);
    }
  }, [pendingPracticeParams, selectedJob, questions]);

  useEffect(() => {
    if (selectedJob && activeTab === 'lab') {
      fetchQuestionsForJob(selectedJob.id);
    }
  }, [selectedJob, activeTab]);

  // Cleanup conversation on unmount
  useEffect(() => {
    return () => {
      if (conversation.status === 'connected') {
        conversation.endSession();
      }
    };
  }, []);

  const fetchJobs = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('job_descriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const jobsData = data || [];
      setJobs(jobsData);
      return jobsData;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setError(error.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobDescriptionSubmit = async () => {
    if (!jobDescriptionUrl.trim()) {
      setError('Please enter a job description URL');
      return;
    }

    // Validate URL format
    try {
      new URL(jobDescriptionUrl);
    } catch (e) {
      setError('Please enter a valid URL');
      return;
    }

    setAddJobStep(1);
    setAddingJob(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Call Supabase Edge Function to extract job description
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('You must be logged in to perform this action');
      }

      const response = await fetch(
        `https://saqouhdsoiavpwknumaw.supabase.co/functions/v1/extract-job-description`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            url: jobDescriptionUrl,
            source: jobSource,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract job description');
      }

      const jobData = await response.json();
      setAddJobStep(2);

      console.log('Job description processed:', {
        company: jobData.company_name,
        title: jobData.job_title,
        questions_generated: jobData.questions_generated
      });

      // Refresh jobs list so the new job appears
      setAddJobStep(3);
      const jobsList = await fetchJobs();
      
      // Reset URL input
      setJobDescriptionUrl('');
      
      // Final step: show success in progress and navigate to job details
      setAddJobStep(4);

      // Navigate to the newly created job details page if we have its ID
      if (jobData.job_description_id && Array.isArray(jobsList)) {
        const createdJob = jobsList.find(j => j.id === jobData.job_description_id);
        if (createdJob) {
          setSelectedJob(createdJob);
          setActiveTab('overview');
          setShowAddJobModal(false);
        }
      }

    } catch (error) {
      console.error('Error processing job description:', error);
      setError(error.message || 'Failed to process job description');
    } finally {
      setAddingJob(false);
    }
  };

  const fetchQuestionsForJob = async (jobId) => {
    setQuestionsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: questionsData, error } = await supabase
        .from('interview_lab_questions')
        .select('*')
        .eq('user_id', user.id)
        .eq('source_type', 'job_description')
        .eq('source_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch latest answer scores for these questions
      const questionIds = (questionsData || []).map(q => q.id);
      const latestScores = {};

      if (questionIds.length > 0) {
        const { data: answersData, error: answersError } = await supabase
          .from('interview_lab_answers')
          .select('question_id, overall_score, created_at')
          .eq('user_id', user.id)
          .in('question_id', questionIds)
          .order('created_at', { ascending: false });

        if (!answersError && answersData) {
          answersData.forEach((answer) => {
            if (
              answer.question_id &&
              typeof answer.overall_score === 'number' &&
              latestScores[answer.question_id] == null
            ) {
              latestScores[answer.question_id] = answer.overall_score;
            }
          });
        }
      }

      // Organize questions by category
      const organized = {
        story_impact: [],
        thinking_skills: [],
        composure_pressure: []
      };

      (questionsData || []).forEach(question => {
        if (organized[question.category]) {
          const score = latestScores[question.id];
          const questionWithScore = typeof score === 'number'
            ? { ...question, latest_answer_score: score }
            : question;
          organized[question.category].push(questionWithScore);
        }
      });

      setQuestions(organized);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleJobClick = (job) => {
    setShowAddJobModal(false);
    setSelectedJob(job);
    setActiveTab('overview');
  };

  const handleBackToJobs = () => {
    setSelectedJob(null);
    setActiveTab('overview');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#28a745';
      case 'medium': return '#ffc107';
      case 'hard': return '#dc3545';
      default: return '#666';
    }
  };

  const handleStartPractice = (question) => {
    setSelectedQuestion(question);
    setShowPracticeModal(true);
  };

  const handleViewFeedback = async (question) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch the most recent answer for this question
      const { data: answerData, error } = await supabase
        .from('interview_lab_answers')
        .select('*')
        .eq('user_id', user.id)
        .eq('question_id', question.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !answerData) {
        setError('No feedback available for this question yet.');
        setTimeout(() => setError(null), 3000);
        return;
      }

      setSelectedAnswer(answerData);
      setSelectedQuestionForFeedback(question);
      setShowFeedbackModal(true);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Failed to load feedback.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleCloseModal = async () => {
    // If we're analyzing, don't close yet
    if (analyzingAnswer) {
      return;
    }

    // If showing analysis results, just close
    if (answerAnalysis) {
      setShowPracticeModal(false);
      setSelectedQuestion(null);
      setAgentState('listening');
      setCurrentConversationId(null);
      setConversationStartTime(null);
      setAnswerAnalysis(null);
      return;
    }

    const hadActiveConversation = conversation.status === 'connected' && currentConversationId && selectedQuestion;
    
    if (conversation.status === 'connected') {
      await conversation.endSession();
    }

    // If there was an active conversation, analyze the answer before closing
    if (hadActiveConversation) {
      setAnalyzingAnswer(true);
      setAnswerAnalysis(null);
      
      try {
        // Calculate duration
        const durationSeconds = conversationStartTime 
          ? Math.floor((Date.now() - conversationStartTime) / 1000)
          : null;

        // Get session for auth
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('You must be logged in to analyze your answer');
        }

        // Call the analyze-interview-answer edge function
        const response = await fetch(
          `https://saqouhdsoiavpwknumaw.supabase.co/functions/v1/analyze-interview-answer`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              conversation_id: currentConversationId,
              question_id: selectedQuestion.id,
              answer_duration_seconds: durationSeconds,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to analyze your answer');
        }

        const analysisData = await response.json();
        setAnswerAnalysis(analysisData);
        
        console.log('✅ Answer analyzed successfully:', analysisData);
        
        // Show success message
        setSuccessMessage(`Answer analyzed! Your score: ${analysisData.overall_score}/100`);
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
        
      } catch (error) {
        console.error('Error analyzing answer:', error);
        setError(error.message || 'Failed to analyze your answer. Your practice session was still recorded.');
        setTimeout(() => {
          setError(null);
        }, 5000);
        // Close modal even if analysis failed
        setShowPracticeModal(false);
        setSelectedQuestion(null);
        setAgentState('listening');
        setCurrentConversationId(null);
        setConversationStartTime(null);
      } finally {
        setAnalyzingAnswer(false);
      }
    } else {
      // No active conversation, just close
      setShowPracticeModal(false);
      setSelectedQuestion(null);
      setAgentState('listening');
      setCurrentConversationId(null);
      setConversationStartTime(null);
      setAnswerAnalysis(null);
    }
  };

  const startConversation = async () => {
    try {
      setAgentState('connecting');

      // Request microphone permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Fetch user's resume data
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAgentState('listening');
        return;
      }

      const { data: resumeData } = await supabase
        .from('resumes')
        .select('parsed_data')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // Format resume data for the agent
      const resumeContext = resumeData?.parsed_data 
        ? JSON.stringify(resumeData.parsed_data, null, 2)
        : 'No resume data available';

      setAgentState('initializing');

      // Start the ElevenLabs conversation session
      // Pass dynamic variables - agent must be configured to use these in its prompt template
      const convId = await conversation.startSession({
        agentId: AGENT_ID,
        connectionType: 'websocket', // Use WebSocket for more stable connection
        dynamicVariables: {
          question: selectedQuestion.question,
          job_title: selectedJob.job_title,
          company_name: selectedJob.company_name,
          job_description: selectedJob.job_description.substring(0, 2000), // Limit length
          resume: resumeContext.substring(0, 2000), // Limit length
        }
      });

      setCurrentConversationId(convId);
      setConversationStartTime(Date.now());
      console.log('✅ Interview call started with agent:', AGENT_ID);
      console.log('✅ Conversation ID:', convId);
    } catch (error) {
      console.error('Error starting interview call:', error);
      alert(`Failed to start interview: ${error.message}`);
      setAgentState('listening');
    }
  };

  const renderQuestions = (categoryQuestions, categoryName) => {
    if (questionsLoading) {
      return (
        <div className="questions-loading">
          <p>Loading questions...</p>
        </div>
      );
    }

    if (categoryQuestions.length === 0) {
      return (
        <div className="empty-state">
          <p>No {categoryName} questions available yet for this job.</p>
        </div>
      );
    }

    return (
      <div className="questions-list">
        {categoryQuestions.map((q, index) => (
          <div key={q.id} className="question-card">
            <div className="question-header">
              <div className="question-number">Q{index + 1}</div>
              <div className="question-meta">
                <span 
                  className="question-difficulty" 
                  style={{ color: getDifficultyColor(q.difficulty) }}
                >
                  {q.difficulty}
                </span>
                <span className="question-duration">⏱ 5 min</span>
                {typeof q.latest_answer_score === 'number' && (
                  <span className="question-score-badge">
                    {q.latest_answer_score}
                  </span>
                )}
              </div>
            </div>
            
            <div className="question-content">
              <h3 className="question-text">{q.question}</h3>
            </div>

            <div className="question-actions">
              {typeof q.latest_answer_score === 'number' && (
                <button 
                  className="view-feedback-button" 
                  onClick={() => handleViewFeedback(q)}
                >
                  View Feedback
                </button>
              )}
              <button className="practice-button" onClick={() => handleStartPractice(q)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="practice-button-icon">
                  <path d="M3 12H5M8 6V18M12 3V21M16 6V18M19 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Start Practice
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="jobs-page">
        <div className="jobs-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-page">
      <div className="jobs-container">
        <div className="jobs-content">
          {/* Breadcrumb when job is selected */}
          {selectedJob ? (
            <>
              <div className="breadcrumb">
                <button className="breadcrumb-link" onClick={handleBackToJobs}>
                  Jobs
                </button>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">
                  {selectedJob.company_name} | {selectedJob.job_title}
                </span>
              </div>

              {/* Tabs */}
              <div className="tabs">
                <button
                  className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={`tab ${activeTab === 'description' ? 'active' : ''}`}
                  onClick={() => setActiveTab('description')}
                >
                  Job Description
                </button>
                <button
                  className={`tab ${activeTab === 'lab' ? 'active' : ''}`}
                  onClick={() => setActiveTab('lab')}
                >
                  Interview Lab
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'overview' ? (
                  selectedJob.ai_resume_feedback ? (
                    <div className="overview-view">
                      {/* Catchphrase */}
                      <div className="overview-catchphrase">
                        <h2>"{selectedJob.ai_resume_feedback.catchphrase}"</h2>
                      </div>

                      {/* Match Score */}
                      <div className="overview-score">
                        <div className="score-circle">
                          <span className="score-number">{selectedJob.ai_resume_feedback.overall_match_score}</span>
                          <span className="score-label">Match Score</span>
                        </div>
                        <div className="score-details">
                          <h3>{selectedJob.job_title}</h3>
                          <p>{selectedJob.company_name}</p>
                        </div>
                      </div>

                      {/* Company Overview */}
                      <div className="overview-section">
                        <h3>Company Overview</h3>
                        <p>{selectedJob.ai_resume_feedback.company_overview}</p>
                      </div>

                      {/* Strengths & Weaknesses */}
                      <div className="overview-grid">
                        <div className="overview-card strengths">
                          <h3>Your Strengths</h3>
                          <ul>
                            {selectedJob.ai_resume_feedback.strengths.map((strength, i) => (
                              <li key={i}>{strength}</li>
                            ))}
                          </ul>
                        </div>

                        <div className="overview-card weaknesses">
                          <h3>Areas to Address</h3>
                          <ul>
                            {selectedJob.ai_resume_feedback.weaknesses.map((weakness, i) => (
                              <li key={i}>{weakness}</li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Analysis */}
                      <div className="overview-section">
                        <h3>The Honest Truth</h3>
                        <div className="analysis-text">{selectedJob.ai_resume_feedback.analysis}</div>
                      </div>

                      {/* Recommendation */}
                      <div className="overview-recommendation">
                        <h3>Verdict</h3>
                        <p>{selectedJob.ai_resume_feedback.recommendation}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>AI analysis is not available for this job. The job may have been added before the AI feature was enabled.</p>
                    </div>
                  )
                ) : activeTab === 'description' ? (
                  <div className="job-description-view">
                    <div className="job-description-header">
                      <h2>{selectedJob.job_title}</h2>
                      <p className="job-company-name">{selectedJob.company_name}</p>
                      <p className="job-saved-date">Saved on {formatDate(selectedJob.created_at)}</p>
                    </div>
                    <div className="job-description-content">
                      <div className="description-text">
                        {selectedJob.job_description}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="interview-lab-view">
                    <div className="lab-intro">
                      <h2>Interview Lab</h2>
                      <p>Practice interview questions tailored for this specific job</p>
                    </div>

                    {/* Lab Tabs */}
                    <div className="lab-tabs">
                      <button
                        className={`lab-tab ${labActiveTab === 'story' ? 'active' : ''}`}
                        onClick={() => setLabActiveTab('story')}
                      >
                        Your Story & Impact
                      </button>
                      <button
                        className={`lab-tab ${labActiveTab === 'skills' ? 'active' : ''}`}
                        onClick={() => setLabActiveTab('skills')}
                      >
                        Your Thinking & Skills
                      </button>
                      <button
                        className={`lab-tab ${labActiveTab === 'pressure' ? 'active' : ''}`}
                        onClick={() => setLabActiveTab('pressure')}
                      >
                        Your Composure Under Pressure
                      </button>
                    </div>

                    {/* Lab Content */}
                    <div className="lab-content">
                      {labActiveTab === 'story' && renderQuestions(questions.story_impact, 'Story & Impact')}
                      {labActiveTab === 'skills' && renderQuestions(questions.thinking_skills, 'Thinking & Skills')}
                      {labActiveTab === 'pressure' && renderQuestions(questions.composure_pressure, 'Composure Under Pressure')}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
            <div className="jobs-header">
              <h1>Jobs</h1>
              <button
                className="add-job-button"
                onClick={() => setShowAddJobModal(true)}
              >
                + Add job
              </button>
            </div>
              <p className="jobs-subtitle">Manage your saved job descriptions</p>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

              {jobs.length === 0 ? (
                <div className="empty-state">
                  <p>No job descriptions saved yet.</p>
                </div>
              ) : (
                <div className="jobs-list">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="job-item"
                      onClick={() => handleJobClick(job)}
                    >
                      <div className="job-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="job-info">
                        <div className="job-info-main">
                          <div>
                            <h3>{job.job_title}</h3>
                            <p className="job-company">{job.company_name}</p>
                            <p className="job-date">{formatDate(job.created_at)}</p>
                          </div>
                          {job.ai_resume_feedback && typeof job.ai_resume_feedback.overall_match_score === 'number' && (
                            <div className="job-score-badge">
                              <span className="job-score-number">
                                {job.ai_resume_feedback.overall_match_score}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Practice Modal */}
      {showPracticeModal && selectedQuestion && (
        <div className="practice-modal-overlay" onClick={handleCloseModal}>
          <div className="practice-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={handleCloseModal}
              disabled={analyzingAnswer}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {analyzingAnswer ? (
              <div className="call-active-container">
                <div className="analyzing-answer">
                  <div className="analyzing-spinner"></div>
                  <p className="call-status">Analyzing your answer...</p>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
                    This may take a few moments
                  </p>
                </div>
              </div>
            ) : answerAnalysis ? (
              <div className="call-active-container">
                <div className="answer-analysis-result">
                  <h3 style={{ fontSize: '20px', marginBottom: '16px' }}>Answer Analysis Complete</h3>
                  <div className="analysis-score">
                    <div className="score-circle" style={{ width: '100px', height: '100px', margin: '0 auto 24px' }}>
                      <span className="score-number" style={{ fontSize: '32px' }}>{answerAnalysis.overall_score}</span>
                      <span className="score-label">Score</span>
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>
                    Your answer has been saved and analyzed. Check the Interview Lab to see detailed feedback.
                  </p>
                  <button className="upload-button" onClick={handleCloseModal}>
                    Close
                  </button>
                </div>
              </div>
            ) : conversation.status !== 'connected' ? (
              <>
                <div className="modal-gradient-circle">
                  <div className="sound-wave"></div>
                  <div className="sound-wave"></div>
                  <div className="sound-wave"></div>
                  <button className="call-ai-button" onClick={startConversation}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.05 5C16.0267 5.19057 16.9244 5.66826 17.6281 6.37194C18.3318 7.07561 18.8095 7.97326 19 8.95M15.05 1C17.0793 1.22544 18.9716 2.13417 20.4163 3.57701C21.8609 5.01984 22.7721 6.91101 23 8.94M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5864 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Call AI agent
                  </button>
                </div>
                
                <div className="modal-question-preview">
                  <h3>Question:</h3>
                  <p>{selectedQuestion.question}</p>
                </div>
              </>
            ) : (
              <div className="call-active-container">
                <BarVisualizerDemo 
                  externalState={agentState}
                  showButtons={false}
                />
                
                <p className="call-status">
                  {agentState === 'connecting' && 'Connecting...'}
                  {agentState === 'initializing' && 'Initializing...'}
                  {agentState === 'listening' && 'Listening...'}
                  {agentState === 'speaking' && 'AI is speaking...'}
                  {agentState === 'thinking' && 'AI is thinking...'}
                </p>
                
                <button className="end-call-button" onClick={handleCloseModal}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9C3 8.44772 3.44772 8 4 8H8L10 3H14L16 8H20C20.5523 8 21 8.44772 21 9V10C21 10.5523 20.5523 11 20 11H4C3.44772 11 3 10.5523 3 10V9Z" fill="currentColor"/>
                    <path d="M4 13H20V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V13Z" fill="currentColor"/>
                  </svg>
                  End Call
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Job Modal */}
      {showAddJobModal && (
        <div
          className="add-job-modal-overlay"
          onClick={() => !addingJob && setShowAddJobModal(false)}
        >
          <div
            className="add-job-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="add-job-modal-close"
              onClick={() => setShowAddJobModal(false)}
              disabled={addingJob}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <h2 className="add-job-modal-title">Add a new job</h2>
            <p className="add-job-modal-subtitle">
              Paste the link to the job description (e.g., LinkedIn, Indeed). We’ll fetch the details and generate tailored interview questions.
            </p>
            <div className="add-job-modal-body">
              {!addingJob ? (
                <>
                  <div className="add-job-source">
                    <p className="add-job-source-label">Source</p>
                    <div className="add-job-source-buttons">
                      <button
                        type="button"
                        className={
                          `add-job-source-button ${jobSource === 'linkedin' ? 'active' : ''}`
                        }
                        onClick={() => setJobSource('linkedin')}
                        disabled={addingJob}
                      >
                        LinkedIn
                      </button>
                      <button
                        type="button"
                        className={
                          `add-job-source-button ${jobSource === 'other' ? 'active' : ''}`
                        }
                        onClick={() => setJobSource('other')}
                        disabled={addingJob}
                      >
                        Other
                      </button>
                    </div>
                  </div>

                  <label className="add-job-modal-label">
                    Job description URL
                    <input
                      type="url"
                      className="url-input"
                      placeholder={
                        jobSource === 'linkedin'
                          ? 'https://www.linkedin.com/jobs/collections/recommended/?currentJobId=4337205810'
                          : 'https://careers.company.com/job/12345'
                      }
                      value={jobDescriptionUrl}
                      onChange={(e) => setJobDescriptionUrl(e.target.value)}
                      disabled={addingJob}
                    />
                  </label>

                  <div className="add-job-modal-actions">
                    <button
                      className="upload-button"
                      onClick={handleJobDescriptionSubmit}
                      disabled={addingJob || !jobDescriptionUrl.trim()}
                    >
                      Add job
                    </button>
                  </div>
                </>
              ) : (
                <div className="add-job-progress">
                  <div className="add-job-progress-bar">
                    <div
                      className="add-job-progress-bar-fill"
                      style={{
                        width: `${(addJobStep / ADD_JOB_STEPS.length) * 100}%`
                      }}
                    />
                  </div>
                  <p className="add-job-progress-text">
                    {ADD_JOB_STEPS[addJobStep - 1]}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Side Panel */}
      {showFeedbackModal && selectedAnswer && selectedQuestionForFeedback && (
        <>
          <div className="feedback-panel-overlay" onClick={() => setShowFeedbackModal(false)}></div>
          <div className="feedback-panel">
            <div className="feedback-panel-header">
              <div className="feedback-panel-header-top">
                <h2 className="feedback-panel-title">Answer Feedback</h2>
                <button className="feedback-panel-close" onClick={() => setShowFeedbackModal(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div className="feedback-panel-header-bottom">
                <p className="feedback-panel-question">{selectedQuestionForFeedback.question}</p>
                {selectedAnswer.overall_score !== null && (
                  <div className="feedback-score-header">
                    <span className="feedback-score-label">Score</span>
                    <div className="score-circle" style={{ width: '80px', height: '80px', marginTop: '8px' }}>
                      <span className="score-number" style={{ fontSize: '28px' }}>{selectedAnswer.overall_score}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="feedback-panel-content">

              {selectedAnswer.ai_feedback && (
                <div className="feedback-details">
                  {selectedAnswer.ai_feedback.strengths && selectedAnswer.ai_feedback.strengths.length > 0 && (
                    <div className="feedback-section">
                      <h3>Strengths</h3>
                      <ul>
                        {selectedAnswer.ai_feedback.strengths.map((strength, i) => (
                          <li key={i}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedAnswer.ai_feedback.weaknesses && selectedAnswer.ai_feedback.weaknesses.length > 0 && (
                    <div className="feedback-section">
                      <h3>Areas for Improvement</h3>
                      <ul>
                        {selectedAnswer.ai_feedback.weaknesses.map((weakness, i) => (
                          <li key={i}>{weakness}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedAnswer.ai_feedback.detailed_feedback && (
                    <div className="feedback-section">
                      <h3>Detailed Feedback</h3>
                      <p className="feedback-text">{selectedAnswer.ai_feedback.detailed_feedback}</p>
                    </div>
                  )}

                  {selectedAnswer.ai_feedback.improvement_suggestions && selectedAnswer.ai_feedback.improvement_suggestions.length > 0 && (
                    <div className="feedback-section">
                      <h3>Improvement Suggestions</h3>
                      <ul>
                        {selectedAnswer.ai_feedback.improvement_suggestions.map((suggestion, i) => (
                          <li key={i}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedAnswer.ai_feedback.key_points_covered && selectedAnswer.ai_feedback.key_points_covered.length > 0 && (
                    <div className="feedback-section">
                      <h3>Key Points Covered</h3>
                      <ul className="key-points-list">
                        {selectedAnswer.ai_feedback.key_points_covered.map((point, i) => (
                          <li key={i}>✓ {point}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedAnswer.ai_feedback.key_points_missing && selectedAnswer.ai_feedback.key_points_missing.length > 0 && (
                    <div className="feedback-section">
                      <h3>Key Points Missing</h3>
                      <ul className="key-points-list missing">
                        {selectedAnswer.ai_feedback.key_points_missing.map((point, i) => (
                          <li key={i}>✗ {point}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Jobs;

